import categoryList from "@/mocks/categoryList.json";

type LearnLink = { name: string; path: string };
type LearnCategory = {
  categoryName: string;
  title: string;
  parentLink: string;
  links: LearnLink[];
};

const trimTrailingSlash = (path: string): string =>
  path.replace(/\/+$/, "") || "/";

const buildPathToSubjectMap = (): Map<string, string> => {
  const map = new Map<string, string>();

  for (const category of categoryList as LearnCategory[]) {
    if (category.parentLink?.startsWith("/learn/")) {
      const hubPath = trimTrailingSlash(category.parentLink);
      if (!map.has(hubPath)) {
        map.set(hubPath, category.title);
      }
    }

    for (const link of category.links) {
      if (!link.path?.startsWith("/learn/")) continue;
      const path = trimTrailingSlash(link.path);
      // First match wins so a topic appearing in multiple categories
      // resolves to the category listed first in categoryList.json.
      if (!map.has(path)) {
        map.set(path, category.title);
      }
    }
  }

  return map;
};

const PATH_TO_SUBJECT = buildPathToSubjectMap();

const stripLocalePrefix = (pathname: string): string => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[1] === "learn") {
    return "/" + parts.slice(1).join("/");
  }
  return "/" + parts.join("/");
};

/**
 * Resolve the Learn page subject for a given pathname so the
 * Get Started With component can pre-populate the subject filter.
 *
 * Returns the matched subject title (e.g. "Computer Science") or
 * `undefined` when the pathname is outside the Learn taxonomy. Callers
 * should treat `undefined` as "no preselection" -- the component must
 * keep working with an empty subject field per the success criteria.
 */
export const getSubjectFromPath = (
  pathname: string | null | undefined,
): string | undefined => {
  if (!pathname) return undefined;

  const normalized = trimTrailingSlash(stripLocalePrefix(pathname));
  if (PATH_TO_SUBJECT.has(normalized)) {
    return PATH_TO_SUBJECT.get(normalized);
  }

  // Walk up extra segments so deeper Learn URLs (e.g.
  // /learn/python/some-subpath) still resolve to the parent topic.
  const segments = normalized.split("/").filter(Boolean);
  while (segments.length > 2) {
    segments.pop();
    const candidate = "/" + segments.join("/");
    if (PATH_TO_SUBJECT.has(candidate)) {
      return PATH_TO_SUBJECT.get(candidate);
    }
  }

  return undefined;
};
